import type { Product, Variant } from '@scayle/storefront-nuxt'
import { computed, watch } from 'vue'
import { useSlideIn } from '#storefront-ui/composables'
import { useState } from '#app/composables/state'
import { type ShoeSizesCountries, SIZE_SELECTOR_SLIDEIN } from '~/constants'
import { useRoute } from '#app/composables/router'

type SizeSelectorTab = ShoeSizesCountries | 'sizeOther'

type SizeSelectorState = {
  product?: Product
  activeVariant?: Variant
  activeSizeTab: SizeSelectorTab
}

export default function useSizeSelector() {
  const state = useState<SizeSelectorState>(
    `global-size-selector-sheet`,
    () => ({
      activeVariant: undefined,
      product: undefined,
      activeSizeTab: 'EU',
    }),
  )

  const {
    toggle: toggleSizeSelector,
    close,
    isOpen,
  } = useSlideIn(SIZE_SELECTOR_SLIDEIN)
  const setActiveVariant = (variant?: Variant) => {
    state.value.activeVariant = variant
  }
  const setProduct = (product?: Product) => {
    if (product) {
      state.value.product = product
      return
    }

    resetState()
  }

  const resetState = () => {
    state.value = {
      ...state.value,
      product: undefined,
      activeVariant: undefined,
    }
  }

  const route = useRoute()
  watch(
    () => route.path,
    () => {
      const isSameVariant =
        state.value.product?.id &&
        route.path.includes(state.value.product?.id.toString()) // avoid excessive triggers

      if (!isSameVariant) {
        close()
        resetState()
      }
    },
    { immediate: true },
  )

  return {
    activeVariant: computed(() => state.value.activeVariant),
    product: computed(() => state.value.product),
    activeSizeTab: computed({
      get: () => state.value.activeSizeTab,
      set: (val) => (state.value.activeSizeTab = val),
    }),
    setActiveVariant,
    setProduct,
    resetState,
    toggleSizeSelector,
    isOpen,
  }
}
